import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Size, t, useChips, type Chip, type SearchFn, type SuggestionRow } from '@bms2/shared';
import { Icon } from '../Icon';
import { Dropdown } from './Dropdown';
import styles from './Dropdown.module.css';
import { removeLabel } from '../removeLabel';

/**
 * Chips / token input (piece 3): a chip row plus the Dropdown that fills it,
 * one component. A chip is removable by its control and by keyboard, and the
 * row announces its count. A replacement, because no browser has a native
 * chip input.
 */
export interface ChipsProps {
  label: string;
  entityLabel: string;
  value: readonly Chip[];
  onChange: (next: Chip[]) => void;
  search: SearchFn;
  onAddNew: () => void;
  disabled?: boolean;
  debounceMs?: number;
  /** 🔒 The label stays for a screen reader and leaves the screen, where the line it stands on names it: the composer's To, Cc and Bcc (decision 1535). */
  labelHidden?: boolean;
}

export const chipsDocumentedStates = ['withItems', 'empty'] as const;

export function Chips({ label, entityLabel, value, onChange, search, onAddNew, disabled, debounceMs, labelHidden = false }: ChipsProps) {
  const chips = useChips({ value, onChange });
  const rowRef = useRef<HTMLDivElement>(null);
  /**
   * 🔨 A pick becomes a chip and the field empties for the next one (T362). 💰 The
   * field kept the picked name, as a single-choice Dropdown does, so a pick read as
   * changing a name rather than adding a person. The field is drawn afresh and keeps
   * the focus.
   */
  const [picks, setPicks] = useState(0);
  useEffect(() => {
    if (picks > 0) rowRef.current?.querySelector<HTMLInputElement>('input')?.focus();
  }, [picks]);
  const onSelect = (row: SuggestionRow) => {
    chips.add({ id: row.id, label: row.line1 });
    setPicks((n) => n + 1);
  };
  const onChipKey = (e: KeyboardEvent<HTMLButtonElement>, id: string) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      chips.remove(id);
    }
  };
  const onRowKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (e.key === 'Backspace' && target instanceof HTMLInputElement && target.value === '') chips.removeLast();
  };
  const countText = `${chips.count} ${t('chips.count')}`;
  return (
    <div ref={rowRef} data-chips={chips.count} onKeyDown={onRowKey}>
      {/* 🔒 The field and its label first, the chips under it (decision 1451). */}
      <Dropdown key={picks} size={Size.Medium} label={label} labelHidden={labelHidden} entityLabel={entityLabel} search={search} onSelect={onSelect} onAddNew={onAddNew} disabled={disabled ?? false} {...(debounceMs === undefined ? {} : { debounceMs })} />
      <div className={styles.chipRow} role="list" aria-label={label}>
        {chips.chips.map((chip) => (
          <span key={chip.id} className={styles.chip} role="listitem" data-chip={chip.id}>
            {chip.label}
            <button
              type="button"
              className={styles.chipRemove}
              aria-label={removeLabel('chips.remove', chip.label)}
              disabled={disabled}
              onClick={() => chips.remove(chip.id)}
              onKeyDown={(e) => onChipKey(e, chip.id)}
            >
              <Icon meaning="remove" size="inline" />
            </button>
          </span>
        ))}
      </div>
      <span className={styles.count} aria-live="polite" data-chip-count={chips.count}>
        {countText}
      </span>
    </div>
  );
}
